import { useEffect, useState } from "react";

type NativeInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "type"
>;

export interface NumericInputProps extends NativeInputProps {
  value: number;
  onValueChange: (value: number) => void;
}

/**
 * Story 11.4-1 — a controlled `type="number"` input that keeps its own text buffer so the user can
 * actually clear the field (delete the "0") and type intermediate states like "5." without it being
 * forced back to 0 on every keystroke. Focus selects the current value so typing replaces it.
 * Emits a finite number (empty → 0); normalises an empty field back to "0" on blur.
 */
export function NumericInput({ value, onValueChange, onFocus, onBlur, ...rest }: NumericInputProps) {
  const [text, setText] = useState<string>(() => String(value));

  // Re-sync the text buffer only when the external numeric value changes to something the buffer
  // does not already represent (e.g. catalog pre-fill, mode switch clearing). Typing does not resync.
  useEffect(() => {
    const parsed = text.trim() === "" ? 0 : Number(text);
    if (!Number.isFinite(parsed) || parsed !== value) {
      setText(String(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <input
      {...rest}
      type="number"
      value={text}
      onFocus={(e) => {
        e.target.select();
        onFocus?.(e);
      }}
      onChange={(e) => {
        const raw = e.target.value;
        setText(raw);
        const v = raw.trim() === "" ? 0 : Number(raw);
        if (Number.isFinite(v)) onValueChange(v);
      }}
      onBlur={(e) => {
        if (text.trim() === "") setText("0");
        onBlur?.(e);
      }}
    />
  );
}
